import * as bootstrap from 'bootstrap';
import a11y from './ts/helpers/a11y';
import intercomHelper from './ts/helpers/intercomHelper';
import tabs from './ts/helpers/tabs';
import header from './ts/parts/header';
import toolbox from './ts/helpers/toolbox';
import copyURL from './ts/helpers/copyURL';
import freeformEvents from './ts/helpers/freeformEvents';
import youtubeHelper from './ts/helpers/youtubeHelper';
import modulesHelper from './ts/helpers/modulesHelper';
import animateOnScroll from './ts/helpers/animateOnScroll';
import modalHelper from './ts/helpers/modalHelper';
import cardHelper from './ts/helpers/cardHelper';
import linksHelper from './ts/helpers/linksHelper';
import embedVideo from './ts/helpers/embedVideo';
import initUserMetrics from './ts/helpers/sessionHelper';

import 'virtual:svg-icons-register';
import { DynamicBlock } from './ts/helpers/dynamicBlock';

class Page {
    private static _instance: Page;
    private loadYTapi = false;

    public static get Instance() {
        return this._instance || (this._instance = new this());
    }

    /**
     * Main init
     */
    public init() {
        initUserMetrics();
        this.listeners();
    }

    /**
     * Init all the low level listeners
     */
    public listeners() {
        // Load scripts that depends on the loaded DOM
        window.addEventListener('DOMContentLoaded', async (event) => {
            freeformEvents();
            modalHelper();
            cardHelper();
            this.loadAnnouncementBanner();
            await this.loadCallback();
        });
        window.addEventListener('load', () => {
            header();
            this.anchorScroll();
        });
        window.scrollTo({ left: 0 });
        window.bootstrap = bootstrap;
    }

    public async loadCallback() {
        await this.createBlockInstances();
        a11y();
        intercomHelper();
        linksHelper();
        embedVideo();
        youtubeHelper();
        modulesHelper();
        toolbox();
        tabs();
        copyURL();
        animateOnScroll();
        this.loadYTiFrameAPI();
    }

    public async createBlockInstances() {
        const importToLoad = new Set();
        // @ts-ignore
        importToLoad.add(import('./ts/components/simplebar-helper.ts'));
        const blocks = document.querySelectorAll(`[data-mod]`);
        blocks.forEach((block: Element) => {
            const blockHtml = <HTMLElement>block;
            const moduleName = blockHtml.dataset.mod ?? 'Block';
            // Dynamically import Splide's carousel files for performance
            if (moduleName === 'carousel' && blocks.length) {
                // @ts-ignore
                importToLoad.add(import('./ts/components/splide-helper.ts'));
            }
            // Load YT Iframe if Hero Homepage needs it
            if (moduleName === 'heroHomepage' && blocks.length) {
                // @ts-ignore
                const blockContent = block.firstElementChild as HTMLElement;
                if (blockContent?.dataset.hasOwnProperty('loadYtIframe')) {
                    this.loadYTapi = true;
                }
            }
            const className = moduleName.charAt(0).toUpperCase() + moduleName.slice(1);
            new DynamicBlock(moduleName, className, block as HTMLElement);
        });
        await Promise.allSettled(importToLoad);
    }

    public loadYTiFrameAPI() {
        if (!this.loadYTapi) return;
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }

    public anchorScroll() {
        if (window.location.hash) {
            const element = document.querySelector(window.location.hash);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({
                        behavior: 'smooth'
                    });
                }, 300);
            }
        }
    }

    private loadAnnouncementBanner() {
        const announcementBanners = document.querySelectorAll(
            '.announcement-banner'
        ) as NodeListOf<HTMLElement>;
        announcementBanners.forEach((banner) => {
            const now = Date.now();
            let showBanner = true;
            const startDate = banner.dataset.start ? new Date(banner.dataset.start) : '';
            const endDate = banner.dataset.end ? new Date(banner.dataset.end) : '';

            if (startDate && endDate) {
                showBanner = now >= startDate && now <= endDate;
            } else if (startDate) {
                showBanner = now >= startDate;
            } else if (endDate) {
                showBanner = now <= endDate;
            }

            if (showBanner) {
                banner.classList.add('show');
            } else {
                banner.remove();
            }
        });
    }
}

// Accept HMR as per: https://vitejs.dev/guide/api-hmr.html
if (import.meta.hot) {
    import.meta.hot.accept(() => {
        console.log('HMR');
    });
}
new Page().init();
